import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AppBanner } from '../components/AppBanner';
import { useComicByShareCode } from '../hooks/useComics';
import { Reactions } from '../components/Reactions';
import { AppStorePromptCard } from '../components/AppStoreBadges';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { ADVENTURE_THEME_LABELS, type ComicPanel, type ComicCharacter } from '@storychain/shared';

const ComicViewerPage: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const { data, isLoading, error } = useComicByShareCode(shareCode);
  const uiLocale = useUILocale();
  useHreflangHead(uiLocale, `/comic/s/${shareCode || ''}`);
  const [selectedCharacter, setSelectedCharacter] = useState<ComicCharacter | null>(null);

  const handleShare = async () => {
    if (!data?.comic) return;

    const shareUrl = `${window.location.origin}/comic/s/${data.comic.shareCode}`;
    const shareText = data.comic.title
      ? `Check out this adventure comic: "${data.comic.title}"`
      : 'Check out this adventure comic on StoryChain!';

    if (navigator.share) {
      try {
        await navigator.share({
          title: data.comic.title || 'StoryChain Comic',
          text: shareText,
          url: shareUrl,
        });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error || !data?.comic) {
    return (
      <Layout title="Comic Not Found">
        <div className="text-center py-12">
          <span className="text-6xl mb-4 block">📚</span>
          <h2 className="text-xl font-semibold mb-2">Comic not found</h2>
          <p className="text-gray-500">
            This comic may have been deleted or made private.
          </p>
        </div>
      </Layout>
    );
  }

  const { comic } = data;
  const themeLabel = ADVENTURE_THEME_LABELS[comic.theme as keyof typeof ADVENTURE_THEME_LABELS] || comic.theme;

  // Filter out first panel if it matches cover (to avoid duplicate)
  const displayPanels = comic.panels.filter(
    (panel, index) => index > 0 || panel.imageUrl !== comic.coverImageUrl
  );

  const getRoleEmoji = (role: ComicCharacter['role']) => {
    switch (role) {
      case 'player': return '🧑';
      case 'villain': return '😈';
      case 'ally': return '🤝';
      case 'creature': return '🐉';
      default: return '👤';
    }
  };

  const getRoleBadgeClass = (role: ComicCharacter['role']) => {
    switch (role) {
      case 'player': return 'bg-blue-100 text-blue-600';
      case 'villain': return 'bg-red-100 text-red-600';
      case 'ally': return 'bg-green-100 text-green-600';
      case 'creature': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const renderPanel = (panel: ComicPanel, index: number) => (
    <div key={index} className="mb-8">
      {/* Panel image */}
      <div className="rounded-xl overflow-hidden shadow-lg mb-4">
        <img
          src={panel.imageUrl}
          alt={`Panel ${index + 1}`}
          className="w-full"
        />
      </div>

      {/* Panel content */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        {/* Panel type badge */}
        {panel.panelType !== 'scene' && (
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full mb-2 ${
            panel.panelType === 'intro' ? 'bg-blue-100 text-blue-600' :
            panel.panelType === 'action' ? 'bg-green-100 text-green-600' :
            panel.panelType === 'twist' ? 'bg-yellow-100 text-yellow-600' :
            panel.panelType === 'climax' ? 'bg-red-100 text-red-600' :
            panel.panelType === 'finale' ? 'bg-purple-100 text-purple-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            {panel.panelType}
          </span>
        )}

        {/* Narrative */}
        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
          {panel.narrative}
        </p>
      </div>
    </div>
  );

  return (
    <Layout title={comic.title}>
      <div className="space-y-6">
        <AppBanner />

        {/* Cover */}
        {comic.coverImageUrl && (
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img
              src={comic.coverImageUrl}
              alt={comic.title}
              className="w-full"
            />
          </div>
        )}

        {/* Title and meta */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h1 className="text-2xl font-bold mb-2">{comic.title}</h1>
          {comic.synopsis && (
            <p className="text-gray-600 mb-4 whitespace-pre-wrap">{comic.synopsis}</p>
          )}
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>{themeLabel}</span>
            <span>👁 {comic.views} views</span>
          </div>

          {/* Reactions */}
          <div className="mt-4">
            <Reactions
              targetType="comic"
              targetId={comic.id}
              reactions={comic.reactions}
              myReaction={data.myReaction}
            />
          </div>

          {/* Authors */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-400 mb-2">Created by</p>
            <div className="flex flex-wrap gap-2">
              {comic.authors.map((author, index) => (
                <span
                  key={index}
                  className="text-sm bg-gray-100 px-3 py-1 rounded-full"
                >
                  {author.characterName || author.displayName}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Characters */}
        {comic.characters && comic.characters.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>👥</span> Characters
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {comic.characters.map((character, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCharacter(character)}
                  className="bg-gray-50 rounded-xl overflow-hidden text-left hover:ring-2 hover:ring-primary-400 transition cursor-pointer"
                >
                  {character.portraitUrl ? (
                    <img
                      src={character.portraitUrl}
                      alt={character.name}
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-4xl">
                      {getRoleEmoji(character.role)}
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{character.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${getRoleBadgeClass(character.role)}`}>
                        {character.role}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2">
                      {character.appearance}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Panels */}
        <div>
          {displayPanels.map((panel, index) => renderPanel(panel, index))}
        </div>

        {/* End card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
          <span className="text-4xl block mb-3">🎬</span>
          <h2 className="text-lg font-semibold mb-2">The End</h2>
          <p className="text-gray-500 text-sm mb-4">
            Enjoyed this adventure? React and share it with friends!
          </p>
          <div className="flex justify-center mb-4">
            <Reactions
              targetType="comic"
              targetId={comic.id}
              reactions={comic.reactions}
              myReaction={data.myReaction}
            />
          </div>
          <button
            onClick={handleShare}
            className="bg-primary-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-700 transition flex items-center justify-center gap-2 mx-auto"
          >
            <span>📤</span>
            Share Comic
          </button>
        </div>

        {/* Download prompt */}
        <AppStorePromptCard message="Want to create your own adventures? Grab the app." />
      </div>

      {/* Character detail modal */}
      {selectedCharacter && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center p-4 pt-8 overflow-y-auto"
          onClick={() => setSelectedCharacter(null)}
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedCharacter(null); }}
            className="self-end mb-2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white text-2xl cursor-pointer"
          >
            ×
          </button>

          <div
            className="max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Portrait */}
            {selectedCharacter.portraitUrl ? (
              <img
                src={selectedCharacter.portraitUrl}
                alt={selectedCharacter.name}
                className="w-full rounded-2xl shadow-2xl mb-4"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-800 rounded-2xl flex items-center justify-center text-8xl mb-4">
                {getRoleEmoji(selectedCharacter.role)}
              </div>
            )}

            {/* Info */}
            <div className="text-center text-white pb-4">
              <div className="flex items-center justify-center gap-3 mb-2">
                <h3 className="text-xl font-bold">{selectedCharacter.name}</h3>
                <span className={`text-sm px-2 py-1 rounded ${getRoleBadgeClass(selectedCharacter.role)}`}>
                  {selectedCharacter.role}
                </span>
              </div>
              <p className="text-white/80 leading-relaxed">
                {selectedCharacter.appearance}
              </p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ComicViewerPage;
